import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Scale, Wheat, Banknote, Heart, BarChart3, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const fmtNum = (n, dec = 1) => Number(n || 0).toLocaleString('id-ID', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtRp = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);

const StatCard = ({ icon: Icon, color, label, value, unit }) => (
  <div className="bg-white rounded-2xl border border-stone-200 p-5">
    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="text-xs text-stone-500 mb-1">{label}</p>
    <p className="text-2xl font-bold text-stone-800">{value}</p>
    {unit && <p className="text-xs text-stone-400 mt-0.5">{unit}</p>}
  </div>
);

export default function ZakatDashboardPane({ profile }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/zakat-ramadan/dashboard`, cfg)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="zakat-dashboard">
      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} color="bg-emerald-600" label="Total Muzakki" value={stats?.total_muzakki || 0} unit="kepala keluarga" />
        <StatCard icon={Scale} color="bg-blue-600" label="Total Jiwa" value={fmtNum(stats?.total_jiwa, 0)} unit="jiwa" />
        <StatCard icon={Wheat} color="bg-amber-600" label="Zakat Beras" value={fmtNum(stats?.total_beras_kg)} unit="kilogram" />
        <StatCard icon={Banknote} color="bg-teal-600" label="Zakat Uang" value={fmtRp(stats?.total_uang_rp)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Heart} color="bg-rose-500" label="Total Infak" value={fmtRp(stats?.total_infak_rp)} />
        <StatCard icon={TrendingUp} color="bg-purple-600" label="Transaksi Hari Ini" value={stats?.total_transaksi_hari_ini || 0} unit="transaksi" />
        <StatCard icon={BarChart3} color="bg-stone-700" label="Total Transaksi" value={stats?.total_transaksi_ramadan || 0} unit="sepanjang Ramadan" />
      </div>

      {/* Daily chart */}
      {stats?.daily_chart?.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="text-sm font-semibold text-stone-700 mb-4">Grafik Transaksi Harian</h3>
          <div className="flex items-end gap-1.5 h-32 overflow-x-auto pb-2">
            {stats.daily_chart.map((day, i) => {
              const maxCount = Math.max(...stats.daily_chart.map(d => d.count), 1);
              const pct = (day.count / maxCount) * 100;
              return (
                <div key={i} className="flex flex-col items-center gap-1 min-w-[32px]">
                  <span className="text-xs text-stone-500">{day.count}</span>
                  <div className="w-6 bg-emerald-500 rounded-t transition-all" style={{ height: `${Math.max(pct * 0.8, 4)}px` }} title={`${day.tanggal}: ${day.count} transaksi`} />
                  <span className="text-xs text-stone-400 rotate-90 origin-center mt-2 whitespace-nowrap" style={{ fontSize: 9 }}>
                    {day.tanggal?.slice(5)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Profile info */}
      {profile && (
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <h3 className="text-sm font-semibold text-stone-700 mb-3">Informasi Amil</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex gap-2"><span className="text-stone-400 w-24">Nama Amil</span><span className="font-medium text-stone-800">{profile.nama_amil}</span></div>
            <div className="flex gap-2"><span className="text-stone-400 w-24">No. HP</span><span className="text-stone-700">{profile.phone || '-'}</span></div>
            <div className="flex gap-2"><span className="text-stone-400 w-24">Alamat</span><span className="text-stone-700">{profile.alamat || '-'}</span></div>
            <div className="flex gap-2"><span className="text-stone-400 w-24">Beras/Jiwa</span><span className="text-emerald-700 font-semibold">{profile.nilai_beras_per_jiwa} kg</span></div>
            <div className="flex gap-2"><span className="text-stone-400 w-24">Uang/Jiwa</span><span className="text-emerald-700 font-semibold">{fmtRp(profile.nominal_uang_per_jiwa)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
